import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { Modal } from "../../../../../components/common/Modal";
import { Button } from "../../../../../components/common/Button";
import { SUBSCRIPTION_PLANS } from "../../../../../constants/common";

type PlanApi = {
    id: number | string;
    name?: string;
    code?: string; // сравниваем с user.subscription.plan
    amount?: number;
    days?: number;
    settings?: any;
};

type PlanMeta = {
    id?: number | string;
    name: string;
    displayName: string;
    description: string;
    features: any[];
    restrictions: any[];
};

type PlanUnified = PlanApi & {
    _displayName?: string;
    _uiDescription?: string;
};

type Option = {
    value: number | string;
    label: string;
    plan: PlanUnified;
};

type Props = {
    isOpen: boolean;
    user: any | null;

    plans: PlanApi[];
    plansPending?: boolean;
    plansError?: boolean;

    onClose: () => void;

    // ✅ оформление/изменение (POST)
    onConfirm: (payload: { planId: number | string; expiresAt: string }) => void;

    // ✅ NEW: отмена (DELETE) — больше не нужно вычислять planId/expiresAt
    onCancel: (payload: { userId: number | string }) => void;
};

// ---- helpers
const pad2 = (n: number) => String(n).padStart(2, "0");

const dateToYmd = (d: Date) =>
    `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const addDays = (base: Date, days: number) => {
    const d = new Date(base);
    d.setDate(d.getDate() + days);
    return d;
};

const pickPlanKey = (p: { code?: any; name?: any } | null | undefined) =>
    String(p?.code ?? p?.name ?? "")
        .trim()
        .toLowerCase();

const pickUserPlanKey = (u: any) =>
    String(u?.subscription?.plan ?? "")
        .trim()
        .toLowerCase();

const CreateSubscriptionModal: React.FC<Props> = ({
                                                      isOpen,
                                                      user,
                                                      plans,
                                                      plansPending,
                                                      plansError,
                                                      onClose,
                                                      onConfirm,
                                                      onCancel,
                                                  }) => {
    // планы только с бэка, тексты — из SUBSCRIPTION_PLANS
    const mergedPlans: PlanUnified[] = useMemo(() => {
        const metaMap = SUBSCRIPTION_PLANS as Record<string, PlanMeta>;

        return (plans ?? []).map((p) => {
            const key = pickPlanKey(p);
            const meta = key ? metaMap[key] : undefined;

            return {
                ...p,
                _displayName: meta?.displayName,
                _uiDescription: meta?.description,
            };
        });
    }, [plans]);

    const options: Option[] = useMemo(() => {
        return mergedPlans.map((p) => ({
            value: p.id,
            label: p._displayName ?? p.name ?? p.code ?? `План #${p.id}`,
            plan: p,
        }));
    }, [mergedPlans]);

    const [selected, setSelected] = useState<Option | null>(null);

    // input[type="date"] хранит "YYYY-MM-DD" — это и отправляем
    const [expiresDate, setExpiresDate] = useState<string>("");

    // при открытии: выбираем текущий план пользователя (если найден), иначе первый
    useEffect(() => {
        if (!isOpen) return;

        const userPlanKey = pickUserPlanKey(user);

        const current =
            (userPlanKey && options.find((o) => pickPlanKey(o.plan) === userPlanKey)) ||
            options?.[0] ||
            null;

        setSelected(current);

        const days = current?.plan?.days;
        if (typeof days === "number" && Number.isFinite(days)) {
            setExpiresDate(dateToYmd(addDays(new Date(), days)));
        } else {
            setExpiresDate("");
        }
    }, [isOpen, options, user]);

    // при смене тарифа — автоподстановка даты, если у плана есть days
    useEffect(() => {
        if (!isOpen) return;
        const days = selected?.plan?.days;
        if (typeof days === "number" && Number.isFinite(days)) {
            setExpiresDate(dateToYmd(addDays(new Date(), days)));
        }
    }, [selected?.value, isOpen]);

    const userLabel = user?.email || user?.name || "пользователя";
    const plan = selected?.plan;

    const confirmDisabled = !user?.id || !selected?.value || !expiresDate || plansPending;

    // ✅ отмена: показываем кнопку только если текущий план не default
    const userPlanKey = pickUserPlanKey(user);
    const canCancel = !!user?.id && !!userPlanKey && userPlanKey !== "default";

    return (
        <Modal
            isOpen={isOpen}
            onClose={plansPending ? undefined : onClose}
            title="Оформить подписку"
        >
            <div className="text-sm text-slate-700">
                Пользователь: <span className="font-semibold">{userLabel}</span>
            </div>

            <div className="mt-4">
                <div className="text-xs font-semibold text-slate-600 mb-2">План подписки</div>

                {plansError ? (
                    <div className="mb-3 text-xs text-red-500">Ошибка загрузки тарифов</div>
                ) : null}

                <Select
                    classNamePrefix="react-select"
                    isDisabled={plansPending || !options.length}
                    isLoading={plansPending}
                    options={options}
                    value={selected}
                    onChange={(opt) => setSelected(opt as Option)}
                    placeholder={plansPending ? "Загрузка тарифов..." : "Выберите тариф"}
                    noOptionsMessage={() => "Нет тарифов"}
                    getOptionValue={(o) => String((o as Option).value)}
                />

                {plan?._uiDescription ? (
                    <div className="mt-3 text-xs text-slate-600 leading-relaxed">{plan._uiDescription}</div>
                ) : null}

                <div className="mt-4">
                    <div className="text-xs font-semibold text-slate-600 mb-2">Дата окончания</div>

                    <input
                        type="date"
                        className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-200"
                        value={expiresDate}
                        onChange={(e) => setExpiresDate(e.target.value)}
                        disabled={plansPending}
                    />

                    {!expiresDate ? <div className="mt-2 text-xs text-slate-500">Выберите дату</div> : null}
                </div>

                {plan ? (
                    <div className="mt-4 text-xs text-slate-500">
                        {typeof plan.amount === "number" ? (
                            <>
                                Цена: <span className="font-semibold text-slate-700">{plan.amount} ₽</span>
                            </>
                        ) : null}
                        {typeof plan.days === "number" ? (
                            <>
                                {typeof plan.amount === "number" ? <span className="mx-2 text-slate-300">•</span> : null}
                                Срок: <span className="font-semibold text-slate-700">{plan.days} дней</span>
                            </>
                        ) : null}
                    </div>
                ) : null}
            </div>

            <div className="mt-5 flex justify-end gap-2">
                {/* ✅ NEW: отмена через DELETE */}
                {canCancel ? (
                    <Button
                        variant="danger"
                        onClick={() => user?.id && onCancel({ userId: user.id })}
                        text="Отменить подписку"
                        disabled={plansPending}
                    />
                ) : null}

                <Button variant="secondary" onClick={onClose} text="Отмена" disabled={plansPending} />

                <Button
                    onClick={() =>
                        selected?.value &&
                        expiresDate &&
                        onConfirm({
                            planId: selected.value,
                            expiresAt: expiresDate, // ✅ "YYYY-MM-DD"
                        })
                    }
                    text={plansPending ? "Загрузка..." : "Продолжить"}
                    disabled={confirmDisabled}
                />
            </div>
        </Modal>
    );
};

export default CreateSubscriptionModal;
